import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGlobalNotifications } from '@/shared/contexts/NotificationContext';
import { RootState } from '@/shared/store/store';
import { NotificationType } from '../types/notification.types';

/**
 * Hook que integra el sistema de notificaciones en tiempo real con el toaster global
 * Muestra un toaster cuando se recibe una nueva notificación via WebSocket
 */
export const useNotificationToaster = () => {
  const { showSuccess, showError, showWarning, showInfo } = useGlobalNotifications();
  const notifications = useSelector((state: RootState) => state.notifications.notifications);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    // Validación defensiva: asegurar que notifications existe y es un array
    if (!notifications || !Array.isArray(notifications) || notifications.length === 0) {
      return; // Salir temprano si no hay notificaciones
    }
    
    // En la primera carga (desde la API), no mostrar toasts
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    
    const lastNotification = notifications[0]; // La más reciente
    
    if (!lastNotification) return;

    // Solo mostrar toaster para notificaciones muy recientes (últimos 2 segundos)
    // Esto asegura que solo se muestren toasts para notificaciones que llegan en tiempo real
    const now = new Date();
    const notificationTime = new Date(lastNotification.createdAt);
    const timeDiff = now.getTime() - notificationTime.getTime();
    
    if (timeDiff > 2000) return; // No mostrar si es muy antigua


    // Mapear tipos de notificación a tipos de toaster
    const getToasterType = (type: NotificationType) => {
      switch (type) {
        case 'success':
          return 'success';
        case 'error':
          return 'error';
        case 'warning':
          return 'warning';
        case 'info':
        default:
          return 'info';
      }
    };

    const toasterType = getToasterType(lastNotification.type);

    // Mostrar toaster según el tipo
    switch (toasterType) {
      case 'success':
        showSuccess(
          lastNotification.title,
          lastNotification.message
        );
        break;
      case 'error':
        showError(
          lastNotification.title,
          lastNotification.message
        );
        break;
      case 'warning':
        showWarning(
          lastNotification.title,
          lastNotification.message
        );
        break;
      case 'info':
      default:
        showInfo(
          lastNotification.title,
          lastNotification.message
        );
        break;
    }
  }, [notifications, showSuccess, showError, showWarning, showInfo]);
};

export default useNotificationToaster;
